import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Box, 
    Typography, 
    TextField, 
    Button, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Checkbox, 
    FormControlLabel,
    IconButton,
    Alert,
    Grid,
    Divider,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { toast } from 'react-toastify';
import { updateFIXSession } from '../../api/fixSessionApi';

// Validation schema - All fields optional for editing
const validationSchema = yup.object({
    // Quote Session
    quoteHost: yup.string().nullable(),
    quotePort: yup.number().nullable().min(1).max(65535),
    quoteSenderCompId: yup.string().nullable(),
    quoteTargetCompId: yup.string().nullable(),
    quoteSenderSubId: yup.string().nullable(),
    quoteTargetSubId: yup.string().nullable(),
    quoteUsername: yup.string().nullable(),
    quotePassword: yup.string().nullable(),
    // Trade Session
    tradeHost: yup.string().nullable(),
    tradePort: yup.number().nullable().min(1).max(65535),
    tradeSenderCompId: yup.string().nullable(),
    tradeTargetCompId: yup.string().nullable(),
    tradeSenderSubId: yup.string().nullable(),
    tradeTargetSubId: yup.string().nullable(),
    tradeUsername: yup.string().nullable(),
    tradePassword: yup.string().nullable(),
    // General Settings
    account: yup.string().nullable(),
    autoReconnect: yup.boolean().nullable(),
    maxReconnectAttempts: yup.number().nullable().min(0),
    reconnectDelaySeconds: yup.number().nullable().min(0),
    timeZone: yup.string().nullable(),
    restartSessionsAfterUpdate: yup.boolean().nullable()
});

const EditFIXSessionModal = ({ open, onClose, onSuccess, lp, fixSession }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Helper function to extract session data from fixSession (which might be an array)
    const getSessionData = () => {
        if (Array.isArray(fixSession) && fixSession.length > 0) {
            // If it's an array, combine data from all sessions
            const combinedData = {
                quoteSession: null,
                tradeSession: null,
                generalSettings: null
            };
            
            // Find sessions with quote and trade data
            fixSession.forEach(session => {
                if (session.quoteSession) {
                    combinedData.quoteSession = session.quoteSession;
                }
                if (session.tradeSession) {
                    combinedData.tradeSession = session.tradeSession;
                }
                if (session.generalSettings) {
                    combinedData.generalSettings = session.generalSettings;
                }
            });
            
            return combinedData;
        }
        return fixSession || {};
    };

    const sessionData = getSessionData();

    // Initialize form with FIX session data
    const formik = useFormik({
        initialValues: {
            // Quote Session
            quoteHost: sessionData?.quoteSession?.host || '',
            quotePort: sessionData?.quoteSession?.port || 8080,
            quoteSenderCompId: sessionData?.quoteSession?.senderCompId || '',
            quoteTargetCompId: sessionData?.quoteSession?.targetCompId || '',
            quoteSenderSubId: sessionData?.quoteSession?.senderSubId || '',
            quoteTargetSubId: sessionData?.quoteSession?.targetSubId || '',
            quoteUsername: sessionData?.quoteSession?.username || '',
            quotePassword: sessionData?.quoteSession?.password || '',
            quoteSslEnabled: sessionData?.quoteSession?.sslEnabled || true,
            quoteHeartbeatInterval: sessionData?.quoteSession?.heartbeatInterval || 30,
            quoteResetOnLogin: sessionData?.quoteSession?.resetOnLogin || true,
            quoteAccount: sessionData?.quoteSession?.account || '',
            // Trade Session
            tradeHost: sessionData?.tradeSession?.host || '',
            tradePort: sessionData?.tradeSession?.port || 8080,
            tradeSenderCompId: sessionData?.tradeSession?.senderCompId || '',
            tradeTargetCompId: sessionData?.tradeSession?.targetCompId || '',
            tradeSenderSubId: sessionData?.tradeSession?.senderSubId || '',
            tradeTargetSubId: sessionData?.tradeSession?.targetSubId || '',
            tradeUsername: sessionData?.tradeSession?.username || '',
            tradePassword: sessionData?.tradeSession?.password || '',
            tradeSslEnabled: sessionData?.tradeSession?.sslEnabled || true,
            tradeHeartbeatInterval: sessionData?.tradeSession?.heartbeatInterval || 30,
            tradeResetOnLogin: sessionData?.tradeSession?.resetOnLogin || true,
            tradeAccount: sessionData?.tradeSession?.account || '',
            // General Settings
            account: sessionData?.generalSettings?.account || '',
            autoReconnect: sessionData?.generalSettings?.autoReconnect || true,
            maxReconnectAttempts: sessionData?.generalSettings?.maxReconnectAttempts || 5,
            reconnectDelaySeconds: sessionData?.generalSettings?.reconnectDelaySeconds || 10,
            timeZone: sessionData?.generalSettings?.timeZone || 'UTC',
            restartSessionsAfterUpdate: true
        },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);

            try {
                // Get token from localStorage
                const token = localStorage.getItem('token');
                if (!token) {
                    throw new Error('Authentication token not found');
                }

                if (!lp || !lp.id) {
                    throw new Error('Liquidity Provider information is missing');
                }

                // Prepare data for API call - use same structure as CREATE operation
                const sessionData = {
                    createQuoteSession: values.quoteHost || values.quotePort || values.quoteSenderCompId || values.quoteTargetCompId || 
                        values.quoteSenderSubId || values.quoteTargetSubId || values.quoteUsername || values.quotePassword,
                    createTradeSession: values.tradeHost || values.tradePort || values.tradeSenderCompId || values.tradeTargetCompId || 
                        values.tradeSenderSubId || values.tradeTargetSubId || values.tradeUsername || values.tradePassword,
                    quoteSessionConfig: (values.quoteHost || values.quotePort || values.quoteSenderCompId || values.quoteTargetCompId || 
                        values.quoteSenderSubId || values.quoteTargetSubId || values.quoteUsername || values.quotePassword) ? {
                        isEnabled: true,
                        host: values.quoteHost || "",
                        port: values.quotePort ? parseInt(values.quotePort) : 8080,
                        senderCompId: values.quoteSenderCompId || "",
                        targetCompId: values.quoteTargetCompId || "",
                        senderSubId: values.quoteSenderSubId || "",
                        targetSubId: values.quoteTargetSubId || "",
                        username: values.quoteUsername || "",
                        password: values.quotePassword || "",
                        sslEnabled: values.quoteSslEnabled !== undefined ? values.quoteSslEnabled : true,
                        heartbeatInterval: values.quoteHeartbeatInterval ? parseInt(values.quoteHeartbeatInterval) : 30,
                        resetOnLogin: values.quoteResetOnLogin !== undefined ? values.quoteResetOnLogin : true,
                        account: values.quoteAccount || values.account || "",
                        additionalSettings: {}
                    } : null,
                    tradeSessionConfig: (values.tradeHost || values.tradePort || values.tradeSenderCompId || values.tradeTargetCompId || 
                        values.tradeSenderSubId || values.tradeTargetSubId || values.tradeUsername || values.tradePassword) ? {
                        isEnabled: true,
                        host: values.tradeHost || "",
                        port: values.tradePort ? parseInt(values.tradePort) : 8080,
                        senderCompId: values.tradeSenderCompId || "",
                        targetCompId: values.tradeTargetCompId || "",
                        senderSubId: values.tradeSenderSubId || "",
                        targetSubId: values.tradeTargetSubId || "",
                        username: values.tradeUsername || "",
                        password: values.tradePassword || "",
                        sslEnabled: values.tradeSslEnabled !== undefined ? values.tradeSslEnabled : true,
                        heartbeatInterval: values.tradeHeartbeatInterval ? parseInt(values.tradeHeartbeatInterval) : 30,
                        resetOnLogin: values.tradeResetOnLogin !== undefined ? values.tradeResetOnLogin : true,
                        account: values.tradeAccount || values.account || "",
                        additionalSettings: {}
                    } : null,
                    generalSettings: {
                        account: values.account || "",
                        autoReconnect: values.autoReconnect !== undefined ? values.autoReconnect : true,
                        maxReconnectAttempts: values.maxReconnectAttempts ? parseInt(values.maxReconnectAttempts) : 5,
                        reconnectDelaySeconds: values.reconnectDelaySeconds ? parseInt(values.reconnectDelaySeconds) : 10,
                        timeZone: values.timeZone || "UTC",
                        globalSettings: {}
                    }
                };

                console.log('Updating FIX Session Data:', sessionData);
                console.log('LP ID being used:', lp.id);
                console.log('FIX Session object:', fixSession);

                // Call API
                const result = await updateFIXSession(token, lp.id, sessionData);
                
                // Success - handle both 200 and 204 responses
                if (result.success || result.message) {
                    toast.success(`FIX session configuration for "${lp.name}" updated successfully!`);
                } else {
                    toast.success(`FIX session configuration for "${lp.name}" updated successfully!`);
                }
                onSuccess && onSuccess(result);
                onClose();
                
            } catch (error) {
                console.error('Error updating FIX session:', error);
                
                // Handle specific error messages from the API
                let errorMessage = 'Failed to update FIX session configuration';
                
                if (error.message) {
                    errorMessage = error.message;
                } else if (error.error) {
                    errorMessage = error.error;
                } else if (typeof error === 'string') {
                    errorMessage = error;
                }
                
                setError(errorMessage);
                toast.error(errorMessage);
            } finally {
                setLoading(false);
            }
        }
    });

    // Reset form when modal opens/closes
    useEffect(() => {
        if (open && fixSession) {
            console.log('EditFIXSessionModal - Received FIX session data:', fixSession);
            console.log('EditFIXSessionModal - Available fields:', Object.keys(fixSession));
            
            const sessionData = getSessionData();
            
            formik.resetForm({
                values: {
                    // Quote Session
                    quoteHost: sessionData?.quoteSession?.host || '',
                    quotePort: sessionData?.quoteSession?.port || 8080,
                    quoteSenderCompId: sessionData?.quoteSession?.senderCompId || '',
                    quoteTargetCompId: sessionData?.quoteSession?.targetCompId || '',
                    quoteSenderSubId: sessionData?.quoteSession?.senderSubId || '',
                    quoteTargetSubId: sessionData?.quoteSession?.targetSubId || '',
                    quoteUsername: sessionData?.quoteSession?.username || '',
                    quotePassword: sessionData?.quoteSession?.password || '',
                    quoteSslEnabled: sessionData?.quoteSession?.sslEnabled || true,
                    quoteHeartbeatInterval: sessionData?.quoteSession?.heartbeatInterval || 30,
                    quoteResetOnLogin: sessionData?.quoteSession?.resetOnLogin || true,
                    quoteAccount: sessionData?.quoteSession?.account || '',
                    // Trade Session
                    tradeHost: sessionData?.tradeSession?.host || '',
                    tradePort: sessionData?.tradeSession?.port || 8080,
                    tradeSenderCompId: sessionData?.tradeSession?.senderCompId || '',
                    tradeTargetCompId: sessionData?.tradeSession?.targetCompId || '',
                    tradeSenderSubId: sessionData?.tradeSession?.senderSubId || '',
                    tradeTargetSubId: sessionData?.tradeSession?.targetSubId || '',
                    tradeUsername: sessionData?.tradeSession?.username || '',
                    tradePassword: sessionData?.tradeSession?.password || '',
                    tradeSslEnabled: sessionData?.tradeSession?.sslEnabled || true,
                    tradeHeartbeatInterval: sessionData?.tradeSession?.heartbeatInterval || 30,
                    tradeResetOnLogin: sessionData?.tradeSession?.resetOnLogin || true,
                    tradeAccount: sessionData?.tradeSession?.account || '',
                    // General Settings
                    account: sessionData?.generalSettings?.account || '',
                    autoReconnect: sessionData?.generalSettings?.autoReconnect || true,
                    maxReconnectAttempts: sessionData?.generalSettings?.maxReconnectAttempts || 5,
                    reconnectDelaySeconds: sessionData?.generalSettings?.reconnectDelaySeconds || 10,
                    timeZone: sessionData?.generalSettings?.timeZone || 'UTC',
                    restartSessionsAfterUpdate: true
                }
            });
            setError(null);
        }
    }, [open, fixSession]);

    const handleClose = () => {
        if (!loading) {
            formik.resetForm();
            setError(null);
            onClose();
        }
    };

    const timeZones = [
        'UTC', 'EST', 'PST', 'CST', 'MST', 'GMT', 'CET', 'EET', 'JST', 'AEST'
    ];

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-fix-session-modal-title"
            aria-describedby="edit-fix-session-modal-description"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: '90%', md: '85%', lg: '80%' },
                maxWidth: 1000,
                maxHeight: '90vh',
                bgcolor: 'rgba(24, 31, 44, 0.95)',
                border: '2px solid rgba(6, 182, 212, 0.3)',
                borderRadius: 3,
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                {/* Header */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EditIcon sx={{ color: '#9c27b0', fontSize: 28 }} />
                        <Box>
                            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 700 }}>
                                Edit FIX Session
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#bfc9db' }}>
                                Update FIX session configuration for {lp?.name || 'this liquidity provider'}
                            </Typography>
                            <Box sx={{ mt: 1, p: 1, background: 'rgba(6, 182, 212, 0.1)', borderRadius: 1, border: '1px solid rgba(6, 182, 212, 0.3)' }}>
                                <Typography variant="body2" sx={{ color: '#1de9b6', fontSize: '0.8rem' }}>
                                    ℹ️ Only the LP ID is required. You can update any fields you want to change - leave others empty to keep current values.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    <IconButton 
                        onClick={handleClose} 
                        disabled={loading}
                        sx={{ color: '#fff' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
                    {error && (
                        <Alert 
                            severity="error" 
                            sx={{ 
                                mb: 3,
                                background: 'rgba(211, 47, 47, 0.1)',
                                border: '1px solid #d32f2f',
                                color: '#fff',
                                '& .MuiAlert-icon': { color: '#ff6b6b' },
                                '& .MuiAlert-message': { 
                                    whiteSpace: 'pre-line',
                                    fontSize: '0.9rem'
                                }
                            }}
                        >
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={formik.handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Quote Session Configuration */}
                            <Grid item xs={12}>
                                <Accordion defaultExpanded sx={{ 
                                    background: 'rgba(0, 207, 255, 0.1)', 
                                    border: '1px solid rgba(0, 207, 255, 0.2)',
                                    '&:before': { display: 'none' }
                                }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#00cfff' }} />}>
                                        <Typography variant="h6" sx={{ color: '#00cfff', fontWeight: 600 }}>
                                            Quote Session Configuration
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quoteHost"
                                                    name="quoteHost"
                                                    label="Host"
                                                    value={formik.values.quoteHost}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quoteHost && Boolean(formik.errors.quoteHost)}
                                                    helperText={formik.touched.quoteHost && formik.errors.quoteHost}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quotePort"
                                                    name="quotePort"
                                                    label="Port"
                                                    type="number"
                                                    value={formik.values.quotePort}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quotePort && Boolean(formik.errors.quotePort)}
                                                    helperText={formik.touched.quotePort && formik.errors.quotePort}
                                                    inputProps={{ min: 1, max: 65535 }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quoteSenderCompId"
                                                    name="quoteSenderCompId"
                                                    label="Sender Comp ID"
                                                    value={formik.values.quoteSenderCompId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quoteSenderCompId && Boolean(formik.errors.quoteSenderCompId)}
                                                    helperText={formik.touched.quoteSenderCompId && formik.errors.quoteSenderCompId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quoteTargetCompId"
                                                    name="quoteTargetCompId"
                                                    label="Target Comp ID"
                                                    value={formik.values.quoteTargetCompId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quoteTargetCompId && Boolean(formik.errors.quoteTargetCompId)}
                                                    helperText={formik.touched.quoteTargetCompId && formik.errors.quoteTargetCompId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quoteSenderSubId"
                                                    name="quoteSenderSubId"
                                                    label="Sender Sub ID"
                                                    value={formik.values.quoteSenderSubId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quoteSenderSubId && Boolean(formik.errors.quoteSenderSubId)}
                                                    helperText={formik.touched.quoteSenderSubId && formik.errors.quoteSenderSubId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quoteTargetSubId"
                                                    name="quoteTargetSubId"
                                                    label="Target Sub ID"
                                                    value={formik.values.quoteTargetSubId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quoteTargetSubId && Boolean(formik.errors.quoteTargetSubId)}
                                                    helperText={formik.touched.quoteTargetSubId && formik.errors.quoteTargetSubId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quoteUsername"
                                                    name="quoteUsername"
                                                    label="Username"
                                                    value={formik.values.quoteUsername}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quoteUsername && Boolean(formik.errors.quoteUsername)}
                                                    helperText={formik.touched.quoteUsername && formik.errors.quoteUsername}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quotePassword"
                                                    name="quotePassword"
                                                    label="Password"
                                                    type="password"
                                                    value={formik.values.quotePassword}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.quotePassword && Boolean(formik.errors.quotePassword)}
                                                    helperText={formik.touched.quotePassword && formik.errors.quotePassword}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="quoteHeartbeatInterval"
                                                    name="quoteHeartbeatInterval"
                                                    label="Heartbeat Interval (seconds)"
                                                    type="number"
                                                    value={formik.values.quoteHeartbeatInterval}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    inputProps={{ min: 1, max: 3600 }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#00cfff' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            id="quoteSslEnabled"
                                                            name="quoteSslEnabled"
                                                            checked={formik.values.quoteSslEnabled}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                color: '#00cfff',
                                                                '&.Mui-checked': { color: '#00cfff' }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ color: '#fff' }}>
                                                            SSL Enabled
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>

                            {/* Trade Session Configuration */}
                            <Grid item xs={12}>
                                <Accordion defaultExpanded sx={{ 
                                    background: 'rgba(29, 233, 182, 0.1)', 
                                    border: '1px solid rgba(29, 233, 182, 0.2)',
                                    '&:before': { display: 'none' }
                                }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#1de9b6' }} />}>
                                        <Typography variant="h6" sx={{ color: '#1de9b6', fontWeight: 600 }}>
                                            Trade Session Configuration
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradeHost"
                                                    name="tradeHost"
                                                    label="Host"
                                                    value={formik.values.tradeHost}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradeHost && Boolean(formik.errors.tradeHost)}
                                                    helperText={formik.touched.tradeHost && formik.errors.tradeHost}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradePort"
                                                    name="tradePort"
                                                    label="Port"
                                                    type="number"
                                                    value={formik.values.tradePort}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradePort && Boolean(formik.errors.tradePort)}
                                                    helperText={formik.touched.tradePort && formik.errors.tradePort}
                                                    inputProps={{ min: 1, max: 65535 }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradeSenderCompId"
                                                    name="tradeSenderCompId"
                                                    label="Sender Comp ID"
                                                    value={formik.values.tradeSenderCompId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradeSenderCompId && Boolean(formik.errors.tradeSenderCompId)}
                                                    helperText={formik.touched.tradeSenderCompId && formik.errors.tradeSenderCompId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradeTargetCompId"
                                                    name="tradeTargetCompId"
                                                    label="Target Comp ID"
                                                    value={formik.values.tradeTargetCompId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradeTargetCompId && Boolean(formik.errors.tradeTargetCompId)}
                                                    helperText={formik.touched.tradeTargetCompId && formik.errors.tradeTargetCompId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradeSenderSubId"
                                                    name="tradeSenderSubId"
                                                    label="Sender Sub ID"
                                                    value={formik.values.tradeSenderSubId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradeSenderSubId && Boolean(formik.errors.tradeSenderSubId)}
                                                    helperText={formik.touched.tradeSenderSubId && formik.errors.tradeSenderSubId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradeTargetSubId"
                                                    name="tradeTargetSubId"
                                                    label="Target Sub ID"
                                                    value={formik.values.tradeTargetSubId}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradeTargetSubId && Boolean(formik.errors.tradeTargetSubId)}
                                                    helperText={formik.touched.tradeTargetSubId && formik.errors.tradeTargetSubId}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradeUsername"
                                                    name="tradeUsername"
                                                    label="Username"
                                                    value={formik.values.tradeUsername}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradeUsername && Boolean(formik.errors.tradeUsername)}
                                                    helperText={formik.touched.tradeUsername && formik.errors.tradeUsername}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradePassword"
                                                    name="tradePassword"
                                                    label="Password"
                                                    type="password"
                                                    value={formik.values.tradePassword}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.tradePassword && Boolean(formik.errors.tradePassword)}
                                                    helperText={formik.touched.tradePassword && formik.errors.tradePassword}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="tradeHeartbeatInterval"
                                                    name="tradeHeartbeatInterval"
                                                    label="Heartbeat Interval (seconds)"
                                                    type="number"
                                                    value={formik.values.tradeHeartbeatInterval}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    inputProps={{ min: 1, max: 3600 }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#1de9b6' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            id="tradeSslEnabled"
                                                            name="tradeSslEnabled"
                                                            checked={formik.values.tradeSslEnabled}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                color: '#1de9b6',
                                                                '&.Mui-checked': { color: '#1de9b6' }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ color: '#fff' }}>
                                                            SSL Enabled
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>

                            {/* General Settings */}
                            <Grid item xs={12}>
                                <Accordion defaultExpanded sx={{ 
                                    background: 'rgba(156, 39, 176, 0.1)', 
                                    border: '1px solid rgba(156, 39, 176, 0.2)',
                                    '&:before': { display: 'none' }
                                }}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#9c27b0' }} />}>
                                        <Typography variant="h6" sx={{ color: '#9c27b0', fontWeight: 600 }}>
                                            General Settings
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="account"
                                                    name="account"
                                                    label="Account"
                                                    value={formik.values.account}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    error={formik.touched.account && Boolean(formik.errors.account)}
                                                    helperText={formik.touched.account && formik.errors.account}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#9c27b0' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' },
                                                        '& .MuiFormHelperText-root': { color: '#ff6b6b' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel sx={{ color: '#bfc9db' }}>Time Zone</InputLabel>
                                                    <Select
                                                        id="timeZone"
                                                        name="timeZone"
                                                        value={formik.values.timeZone}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        sx={{
                                                            color: '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#9c27b0' },
                                                            '& .MuiSvgIcon-root': { color: '#bfc9db' }
                                                        }}
                                                    >
                                                        {timeZones.map((tz) => (
                                                            <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="maxReconnectAttempts"
                                                    name="maxReconnectAttempts"
                                                    label="Max Reconnect Attempts"
                                                    type="number"
                                                    value={formik.values.maxReconnectAttempts}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    inputProps={{ min: 0, max: 100 }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#9c27b0' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    id="reconnectDelaySeconds"
                                                    name="reconnectDelaySeconds"
                                                    label="Reconnect Delay (seconds)"
                                                    type="number"
                                                    value={formik.values.reconnectDelaySeconds}
                                                    onChange={formik.handleChange}
                                                    onBlur={formik.handleBlur}
                                                    inputProps={{ min: 0, max: 3600 }}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            color: '#fff',
                                                            '& fieldset': { borderColor: 'rgba(6, 182, 212, 0.3)' },
                                                            '&:hover fieldset': { borderColor: 'rgba(6, 182, 212, 0.5)' },
                                                            '&.Mui-focused fieldset': { borderColor: '#9c27b0' }
                                                        },
                                                        '& .MuiInputLabel-root': { color: '#bfc9db' }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            id="autoReconnect"
                                                            name="autoReconnect"
                                                            checked={formik.values.autoReconnect}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                color: '#9c27b0',
                                                                '&.Mui-checked': { color: '#9c27b0' }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ color: '#fff' }}>
                                                            Auto Reconnect
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            id="restartSessionsAfterUpdate"
                                                            name="restartSessionsAfterUpdate"
                                                            checked={formik.values.restartSessionsAfterUpdate}
                                                            onChange={formik.handleChange}
                                                            sx={{
                                                                color: '#9c27b0',
                                                                '&.Mui-checked': { color: '#9c27b0' }
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Typography sx={{ color: '#fff' }}>
                                                            Restart Sessions After Update
                                                        </Typography>
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>
                            </Grid>
                        </Grid>
                    </form>
                </Box>

                {/* Footer */}
                <Box sx={{
                    p: 3,
                    borderTop: '1px solid rgba(6, 182, 212, 0.2)',
                    background: 'rgba(6, 182, 212, 0.1)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            color: '#bfc9db',
                            borderColor: 'rgba(6, 182, 212, 0.3)',
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: 'rgba(6, 182, 212, 0.5)',
                                background: 'rgba(6, 182, 212, 0.1)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={formik.handleSubmit}
                        disabled={loading || !formik.isValid}
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{
                            background: '#9c27b0',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': {
                                background: '#7b1fa2'
                            },
                            '&:disabled': {
                                background: 'rgba(156, 39, 176, 0.3)',
                                color: 'rgba(255, 255, 255, 0.3)'
                            }
                        }}
                    >
                        {loading ? 'Updating...' : 'Update FIX Session'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

export default EditFIXSessionModal;
